import { Badge, Button, Card, Carousel } from "react-bootstrap";
import { BuildingIcon, CalculatorIcon, CameraIcon, CapIcon, ChartIcon, CircleTickIcon, ClientIcon, CriticalPathIcon, CrossIcon, DateIcon, DiscIcon, DollarIcon, DownloadIcon, GeminiIcon, LinkIcon, RupeeIcon, SparkleIcon, TrackingIcon, UploadFileIcon } from "../../../shared/icons/Icons";

import '../home/LandingPage.scss';
import { useRef, useState } from "react";
import { APP_LOGO_PATH } from '../../../shared/settings/config';

const cauroselData = [
    {
        icon: GeminiIcon,
        title: "Gemini 3 Flash Technology",
        description: "AI-Powered Estimation",
        details: "Upload your construction diagrams and get instant AI-powered analysis. Our advanced Gemini AI analyze floor plans, detects elements, and generates accurate material & labor estimates in seconds.",
        features: [
            "Instant Diagram analysis",
            "Smart Element Detection",
            "Accurate Cost Prediction"
        ],
        image: `${process.env.PUBLIC_URL}/static/images/slide1.jpeg`,
        gradient: "linear-gradient(to right, #7c3aed, rgba(67,56,202,0.4))"
    },
    {
        icon: CriticalPathIcon,
        title: "Critical Path Method Analysis",
        description: "CPM Project Planning",
        details: "Plan your construction projects with professional CPM analysis. Forward/backward pass calculations, critical path identification, and MS Excel export for seamless project management.",
        features: [
            "Construction task templates",
            "Critical path detection",
            "MS Excel export"
        ],
        image: `${process.env.PUBLIC_URL}/static/images/slide2.jpeg`,
        gradient: "linear-gradient(to right, #4f46e5, rgba(126,34,206,0.4))"
    },
    {
        icon: CapIcon,
        title: "Man-Days Calculation & Tracking",
        description: "Labour Management",
        details: "Manage your workforce efficiently with labour categories, skill-based rates, and automatic man-days calculation. Configure rates per project and track labour costs accurately.",
        features: [
            "Labour categories",
            "Skill-based rates",
            "Custom rates per project"
        ],
        image: `${process.env.PUBLIC_URL}/static/images/slide3.jpeg`,
        gradient: "linear-gradient(to right, #0d9488, rgba(14,116,144,0.4))"
    },
    {
        icon: DateIcon,
        title: "Holiday-Aware Project Timelines",
        description: "Smart Scheduling",
        details: "Create accurate project schedules with India national holidays and weekly offs. Integrate with your ERP system for material supply tracking and seamless data sync.",
        features: [
            "India holiday calendar",
            "Working days calculation",
            "ERP integration"
        ],
        image: `${process.env.PUBLIC_URL}/static/images/slide4.jpeg`,
        gradient: "linear-gradient(to right, #d97706, rgba(202,138,4,0.4))"
    }
];

const ProjectData = [
    {
        icon: ClientIcon,
        color: "linear-gradient(to bottom right, #a855f7, #4f46e5)",
        heading: "Client Management",
        details: "Organize clients with contact details, GST info & project history"
    },
    {
        icon: UploadFileIcon,
        color: "linear-gradient(to bottom right, #3b82f6, #0891b2)",
        heading: "AI Diagram Analysis",
        details: "Upload floor plans & get instant AI-powered analysis"
    },
    {
        icon: CalculatorIcon,
        color: "linear-gradient(to bottom right, #f97316, #d97706)",
        heading: "Smart Estimation",
        details: "Accurate estimations of quantities with Gemini AI"
    },
    {
        icon: DollarIcon,
        color: "linear-gradient(to bottom right, #22c55e, #0d9488)",
        heading: "Material Management",
        details: "Accurate material quantity with Gemini AI"
    },
    {
        icon: CapIcon,
        color: "linear-gradient(to bottom right, #10b981, #0d9488)",
        heading: "Labour Management",
        details: "Labour categories with man-days calculation & skill levels"
    },
    {
        icon: CriticalPathIcon,
        color: "linear-gradient(to bottom right, #8b5cf6, #9333ea)",
        heading: "CPM Project Planning",
        details: "Critical path analysis with ES/EF/LS/LF & float calculation"
    },
    {
        icon: DateIcon,
        color: "linear-gradient(to bottom right, #ec4899, #e11d48)",
        heading: "Smart Scheduling",
        details: "Holiday-aware timelines with India national holidays"
    },
    {
        icon: LinkIcon,
        color: "linear-gradient(to bottom right, #06b6d4, #2563eb)",
        heading: "ERP Integration",
        details: "Sync materials & labour with your external ERP system"
    },
    {
        icon: DownloadIcon,
        color: "linear-gradient(to bottom right, #6366f1, #7c3aed)",
        heading: "MS Excel Export",
        details: "Export project plans to Microsoft Excel  format"
    },
    {
        icon: TrackingIcon,
        color: "linear-gradient(to bottom right, #14b8a6, #059669)",
        heading: "Progress Tracking",
        details: "Track construction layers: Foundation to Handover"
    },
    {
        icon: CameraIcon,
        color: "linear-gradient(to bottom right, #f59e0b, #ea580c)",
        heading: "Photo Assessments",
        details: "Capture progress photos with detailed assessments"
    },
    {
        icon: ChartIcon,
        color: "linear-gradient(to bottom right, #ef4444, #ea580c)",
        heading: "Cost Overrun Alerts",
        details: "Real-time budget tracking with variance analysis"
    }
]

const WorkflowData = [
    {
        id: "01",
        icon: ClientIcon,
        color: "linear-gradient(to bottom right, #a855f7, #4f46e5)",
        heading: "Add Client",
        details: "Create client profile with company details & GST number"
    },
    {
        id: "02",
        icon: UploadFileIcon,
        color: "linear-gradient(to bottom right, #a855f7, #4f46e5)",
        heading: "Upload Diagrams",
        details: "Upload construction diagram for AI analysis"
    },
    {
        id: "03",
        icon: CalculatorIcon,
        color: "linear-gradient(to bottom right, #a855f7, #4f46e5)",
        heading: "Get Estimation",
        details: "Receive detailed estimates"
    },
    {
        id: "04",
        icon: CapIcon,
        color: "linear-gradient(to bottom right, #a855f7, #4f46e5)",
        heading: "Calculate Materials",
        details: "Receive detailed material quantity"
    },
    {
        id: "05",
        icon: CapIcon,
        color: "linear-gradient(to bottom right, #a855f7, #4f46e5)",
        heading: "Calculate Labour",
        details: "Get man-days & labour cost breakdown by skill level"
    },
    {
        id: "06",
        icon: CriticalPathIcon,
        color: "linear-gradient(to bottom right, #a855f7, #4f46e5)",
        heading: "Create CPM Plan",
        details: "Generate critical path with construction tasks"
    },
    {
        id: "07",
        icon: DateIcon,
        color: "linear-gradient(to bottom right, #a855f7, #4f46e5)",
        heading: "Schedule Project",
        details: "Set timeline with holidays & ERP sync"
    },
    {
        id: "08",
        icon: TrackingIcon,
        color: "linear-gradient(to bottom right, #a855f7, #4f46e5)",
        heading: "Track Progress",
        details: "Monitor layer-wise construction progress"
    },
    {
        id: "09",
        icon: DollarIcon,
        color: "linear-gradient(to bottom right, #a855f7, #4f46e5)",
        heading: "Generate Invoice",
        details: "Create & send professional invoices"
    }
]

const ConstructionWorkflowData = [
    "Complete client & project management",
    "AI-powered diagram analysis (Gemini 3 Flash)",
    "Labour categories with man-days calculation",
    "CPM analysis with critical path identification",
    "India holiday-aware project scheduling",
    "ERP integration for material supply sync",
    "MS Excel export for compatibility",
    "Layer-wise progress tracking with photo capture",
    "Multi-level assessment approval workflow",
    "Real-time cost variance & overrun detection",
    "Professional invoice generation with GST",
    "Comprehensive final reports with AI analysis"
]

const pricingPlans = [
    {
        title: "Free",
        price: "₹0",
        duration: "forever",
        description: "Perfect for getting started with basic estimation",
        features: [
            "Rs 0 at registration",
            "Perfect for testing the accuracy",
            "3 drawings ",
            "Advanced AI analysis",
            "Estimation of quantities",
            "Excel downloads of detailed quantity estimation",
            "Cost Estimation",
            "Standard construction rates",
            "Standard Material rates",
            "Standard Labour rates"
         
        ],
        disabledFeatures: [
            "Estimation of material quantities for each activity",
            "Estimation of Labour for each activity",
            "CPM Excel download for construction management",
            "CPM Charts download"
        ],
        buttonText: "Get Started Free",
        highlight: false
    },
    {
        title: "Standard",
        price: "₹3,000",
        duration: "/building",
        description: "For individual contractors and small teams",
        features: [
           "Independent houses upto 2 floors",
           "Perfect for small residential buildings",
           "Estimation for 1 (G+1)buiding ",
           "Advanced AI analysis",
           "Estimation of quantities",
           "Excel downloads of detailed quantity estimation",
           "Cost Estimation",
           "Standard construction rates",
           "Standard Material rates",
           "Standard Labour rates"
           

        ],
        disabledFeatures: [
            "Estimation of material quantities for each activity",
            "Estimation of Labour for each activity",
            "CPM Excel download for construction management",
            "CPM Charts download"
        ],
        buttonText: "Start Free Trial",
        highlight: false
    },
    {
        title: "Premium",
        price: "₹8,000",
        duration: "/building",
        description: "Built for growing construction businesses",
        features: [
           "Apartments/multistoried buildings  above 2 floors and upto 6 floors",
           "Perfect for Apartment buildings",
           "Estimation for 1 (G+6)buiding/block",
           "Advanced AI analysis",
           "Estimation of quantities",
           "Excel downloads of detailed quantity estimation",
           "Cost Estimation",
           "Standard construction rates",
           "Standard Material rates",
           "Standard Labour rates",
           "Estimation of material quantities for each activity",
           "Estimation of Labour for each activity",
           
        ],
        disabledFeatures: [
            "CPM Excel download for construction management",
            "CPM Charts download"
        ],
        buttonText: "Start Free Trial",
        highlight: false,
        // badge: "Most Popular"
    },
    {
        title: "Enterprise",
        price: "Custom",
        duration: "pricing",
        description: "For large organizations with custom needs",
        features: [
           "Custom pricing",
           "For large organisations with custom needs",
           "Everything in Premium",
           "CPM Excel download for construction management",
           "CPM Charts download",
           "ERP integration",
           "Unlimited team members",
           "Custom integrations",
           "On-premise deployment",
           "SLA guarantee (99.9%)",
           "Custom training",
           "24/7 priority support",
           

        ],
        disabledFeatures: [],
        buttonText: "Start Free Trial",
        highlight: false
    }
];

export default function LandingPage(props) {

    const featuresRef = useRef(null);
    const pricingRef = useRef(null);

    const [menuOpen, setMenuOpen] = useState(false);

    const closeMenu = () => setMenuOpen(false);

    return (
        <div className="landing-main-content">
            <nav class={`navbar navbar-expand-md px-3 ${menuOpen ? "menu-open" : ""}`}>
                <div class="d-flex justify-content-sm-between max-container">
                    <img className="w-8rem" src={APP_LOGO_PATH} alt="Logo" />

                    <button class="navbar-toggler" type="button" onClick={() => setMenuOpen(!menuOpen)} aria-label="Toggle navigation">
                        <span class="navbar-toggler-icon"></span>
                    </button>

                    <div class="collapse navbar-collapse" id="navMenu">
                        <ul class="navbar-nav ms-auto align-items-lg-center gap-lg-3 clickable">
                            <li class="nav-item fw-bold"><span class="nav-link" onClick={() => { featuresRef.current.scrollIntoView({ behavior: "smooth" }); closeMenu(); }}>Features</span></li>
                            <li class="nav-item fw-bold"><span class="nav-link" onClick={() => { pricingRef.current.scrollIntoView({ behavior: "smooth" }); closeMenu(); }}>Pricing</span></li>
                            <li class="nav-item fw-bold"><a class="nav-link" href="/sign-in" onClick={closeMenu}>Login</a></li>
                            <li class="nav-item fw-bold">
                                <a class="btn btn-sm btn-light rounded-pill px-3 mt-2 mt-lg-0" href="/sign-up" onClick={closeMenu}>Get Started</a>
                            </li>
                            <li class="nav-item fw-bold"><a class="btn btn-sm btn-light rounded-pill px-3 mt-2 mt-lg-0" href="/user-agreement" onClick={closeMenu} style={{background: "#014bb0","color":"white" , "border-color": "#014bb0" }}>User Agreement</a></li>
                        </ul>
                    </div>
                </div>
            </nav>

            <Carousel interval={3000}>
                {cauroselData.map((item, index) => {
                    const Icon = item.icon;

                    return (
                        <Carousel.Item key={index}>
                            <div className="carousel-slide gap-3" style={{ backgroundImage: `${item.gradient}, url(${item.image})`, backgroundSize: "cover", backgroundPosition: "center", backgroundRepeat: "no-repeat" }}>
                                <div className="carousel-content">
                                    <div className="d-flex flex-column gap-3 text-white">
                                        <div className="d-flex align-items-center gap-2 mb-2">
                                            <Icon />
                                            <div className="text-uppercase text-n">{item.title}</div>
                                        </div>

                                        <div className="fw-bold text-5l">{item.description}</div>

                                        <div className="text-l">{item.details}</div>

                                        <div className="d-flex flex-wrap gap-3 mt-3">
                                            {item.features.map((data, i) => (
                                                <span key={i} className="feature-pill text-n">
                                                    <CircleTickIcon /> {data}
                                                </span>
                                            ))}
                                        </div>

                                        <div>
                                            <Button className="mt-4 rounded-pill px-4 w-auto text-l fw-bold border-0" onClick={() => {props.history.push('/sign-up')}}>
                                                Get Started Now →
                                            </Button>
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </Carousel.Item>
                    );
                }
                )}
            </Carousel>

            <div className="d-flex flex-column flex-md-row align-items-center justify-content-center max-container gap-3" style={{ marginTop: "7rem", marginBottom: "5rem" }}>
                <div className="w-40 d-flex flex-column align-items-start  gap-3 resp-width-40">
                    <span className="d-flex align-items-center bg-construction mb-3 gap-3">
                        <SparkleIcon color="#FF8E53" /><span>Complete Construction Management Suite</span>
                    </span>

                    <h1 className="display-4 fw-bold">
                        AI-Powered <br />
                        <span className="text-gradient">Construction</span> Estimator
                    </h1>

                    <div className="mt-3 text-l">
                        From client onboarding to final invoicing — CivilVertex AI handles your entire construction workflow. AI estimation, CPM planning, labour management, holiday-aware scheduling, and MS Excel export all in one platform.
                    </div>

                    <div className="d-flex gap-3 mt-4">
                        <Button className="primary-gradient-btn px-4 rounded-pill text-l" onClick={() => {props.history.push('/sign-up')}}>
                            Start Free Trial →
                        </Button>
                        <Button className="px-4 rounded-pill text-l gradient-border" onClick={() => {props.history.push("/sign-in")}}>
                            Login to Dashboard
                        </Button>
                    </div>

                    <div className="mt-4 d-flex gap-4 small">
                        <span className="d-flex align-items-center gap-2 text-muted text-n"><CircleTickIcon color="#10b981" /> No credit card required</span>
                        <span className="d-flex align-items-center gap-2 text-muted text-n"><CircleTickIcon color="#10b981" /> MS Excel Export</span>
                        <span className="d-flex align-items-center gap-2 text-muted text-n"><CircleTickIcon color="#10b981" /> ERP Integration</span>
                    </div>
                </div>

                <div className="w-60 d-flex justify-content-center position-relative resp-width-60 resp-mt-3">
                    <div className="image-card">
                        <img src="/static/images/slide1.jpeg" alt="hero" className="sec-img" />
                    </div>

                    <div className="d-flex floating-card top-card gap-3">
                        <div className="d-flex">
                            <div className="summary-icon-green p-2 rounded"><DiscIcon color="#FFF" /></div>
                        </div>
                        <div className="d-flex flex-column align-items-start">
                            <span className="text-gray fw-bold">Estimation Accuracy</span>
                            <span className="fw-bold">98%</span>
                        </div>
                    </div>

                    <div className="d-flex floating-card middle-card gap-3">
                        <div className="d-flex">
                            <div className="summary-icon-green p-2 rounded"><RupeeIcon color="#FFF" /></div>
                        </div>
                        <div className="d-flex flex-column">
                            <span className="text-gray fw-bold">Cost Tracked</span>
                            <span className="fw-bold">₹500Cr+</span>
                        </div>
                    </div>

                    <div className="d-flex floating-card bottom-card gap-3">
                        <div className="d-flex">
                            <div className="summary-icon-purple p-2 rounded"><BuildingIcon color="#FFF" /></div>
                        </div>
                        <div className="d-flex flex-column">
                            <span className="text-gray fw-bold">Projects Managed</span>
                            <span className="fw-bold">10,000+</span>
                        </div>
                    </div>
                </div>
            </div>

            <div id="features" ref={featuresRef} className="manage-projects">
                <div className="max-container text-center mb-5" style={{ marginTop: "5rem" }}>
                    <h2 className="text-4l mt-5 fw-bold">Everything You Need to <span className="text-gradient">Manage Projects</span></h2>
                    <h4 className="mt-3 mb-5 text-muted">A complete suite of tools for construction professionals — from estimation to invoicing</h4>

                    <div className="row g-4">
                        {ProjectData.map((item, index) => {
                            const Icon = item.icon;
                            return (
                                <div className="col-lg-3 col-md-6 col-sm-12">
                                    <div key={index} className="project-card metric-card-view">
                                        <div className="icon-box" style={{ background: item.color }}>
                                            <Icon color="#FFF" />
                                        </div>
                                        <div className="text-l mt-3 fw-bold">{item.heading}</div>
                                        <div className="text-muted">{item.details}</div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            </div>

            <div className="max-container text-center" style={{ marginTop: "7rem", marginBottom: "7rem" }}>
                <h2 className="text-4l mt-5 fw-bold">Simple <span className="text-secondary">6-Step</span> Workflow</h2>
                <h4 className="mt-3 mb-5">From client setup to final invoice — streamlined for efficiency</h4>

                <div className="row g-4">
                    {WorkflowData.map((item, index) => {
                        const Icon = item.icon;
                        return (
                            <div className="col-lg-4 col-md-8 col-sm-16">
                                <div key={index} className="project-card metric-card-view d-flex gap-3">
                                    <div className="gradient-text">{item.id}</div>
                                    <div className="d-flex flex-column align-items-start gap-2">
                                        <div className="workflow-icon-box" style={{ background: item.color }}>
                                            <Icon color="#FFF" />
                                        </div>
                                        <div className="text-l mt-3 fw-bold">{item.heading}</div>
                                        <div className="text-muted">{item.details}</div>
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>

            <div className="d-flex flex-column construction-container manage-projects">
                <div className="d-flex flex-column flex-md-row align-items-center justify-content-center gap-5 w-100 resp-width-75">
                    <div className="w-35 d-flex flex-column align-items-start gap-3 resp-width-40" style={{ marginTop: "5rem" }}>
                        <h2 className="display-4 fw-bold">
                            Built for <span className="text-secondary">Construction Professionals</span>
                        </h2>

                        <h5 className="text-muted mt-3">
                            Whether you're a civil engineer, contractor, or project manager, CivilVertex AI provides enterprise-grade tools for accurate estimations and project tracking.
                        </h5>

                        {
                            ConstructionWorkflowData.map((item, index) => (
                                <div key={index} className="d-flex align-items-center gap-2">
                                    <div className="summary-icon-green p-1 rounded"><CircleTickIcon color="white" /></div>
                                    <span>{item}</span>
                                </div>
                            ))
                        }
                    </div>

                    <div className="w-35 d-flex flex-column summary-gradient resp-width-40 resp-mt-3">
                        <SparkleIcon color="#FF8E53" size="24" />
                        <div className="d-flex flex-column gap-3">
                            <h3 className="fw-bold">Ready to transform your workflow?</h3>
                            <p>Join thousands of construction professionals using CivilVertex AI to streamline their estimation process.</p>
                            <Button className="btn-primary rounded-pill px-4 w-auto" onClick={() => {props.history.push('/sign-up')}}>
                                Create Free Account →
                            </Button>
                        </div>
                        <hr />
                        <div className="d-flex align-items-center justify-content-center gap-5">
                            <div>
                                <h2 className="text-white">10K +</h2>
                                <span>Projects</span>
                            </div>
                            <div>
                                <h2 className="text-white">5K +</h2>
                                <span>Users</span>
                            </div>
                            <div>
                                <h2 className="text-white">98%</h2>
                                <span>Accuracy</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div id="pricing" ref={pricingRef} style={{ marginTop: "13rem" }}>
                    <div className="d-flex flex-column align-items-center justify-content-center">
                        <span className="bg-construction mb-3 gap-5">
                            <RupeeIcon color="#FF8E53" />Simple Pricing
                        </span>
                        <div className="text-4l fw-bold">Choose Your <span className="text-gradient">Perfect Plan</span></div>
                        <div className="text-l mt-3 text-muted">
                            Start free and scale as you grow. All plans include a 14-day free trial.
                        </div>
                    </div>
                    <div className="container py-5">
                        <div className="row g-4">
                            {pricingPlans.map((plan, index) => (
                                <div className="col-md-6 col-lg-3" key={index}>
                                    <Card className={`h-100 text-center border-0 pricing-card ${plan.highlight ? "border border-warning" : "" }`}>
                                        <Card.Body className="d-flex flex-column">
                                            {plan.badge && (
                                                <Badge bg="warning" text="dark" className="mb-2">
                                                    {plan.badge}
                                                </Badge>
                                            )}

                                            <h5 className="fw-bold">{plan.title}</h5>

                                            <h2 className="my-3 text-3l fw-bold"> {plan.price}{" "}
                                                <small className="fs-6 text-muted">{plan.duration}</small>
                                            </h2>

                                            <p className="text-muted fs-6 ">{plan.description}</p>

                                            <ul className="list-unstyled text-start flex-grow-1 mb-0">
                                                {plan.features.map((feature, i) => (
                                                    <li key={i} className="d-flex gap-3 mb-2 text-muted fw-bold align-items-start fs-6">
                                                        <span className="flex-shrink-0"><CircleTickIcon color="rgb(16 185 129)" size="1.25rem" /></span> {feature}
                                                    </li>
                                                ))}

                                                {plan.disabledFeatures.map((feature, i) => (
                                                    <li key={i} className="d-flex gap-3 mb-2 text-secondary align-items-start">
                                                        <span className="flex-shrink-0"><CrossIcon size="1.25rem" /></span> {feature}
                                                    </li>
                                                ))}
                                            </ul>

                                            <Button className={`w-100 mt-auto rounded-pill ${plan.highlight ? "primary-gradient-btn" : "btn-secondary"}`} onClick={() => {props.history.push("/sign-up")}}>
                                                {plan.buttonText}
                                            </Button>
                                        </Card.Body>
                                    </Card>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="construction-container flex-column gap-3 mb-5" style={{ background: "linear-gradient(135deg, #0029ab 0%, #001f7a 100%)" }}>
                    <h1 className="text-4l fw-bold text-white mt-5">Start Managing Your Construction Projects Today</h1>
                    <h4 className="text-white">Get instant access to AI estimation, CPM planning, labour management, and smart scheduling. 
                        Export to MS Excel & sync with your ERP. No credit card required.</h4>
                    <div className="d-flex gap-3 mb-5">
                        <Button className="primary-gradient-btn mt-4 rounded-pill px-4 w-auto" onClick={() => {props.history.push('/sign-up')}}>
                            Get Started Free →
                        </Button>
                        <Button className="btn-primary mt-4 rounded px-4 w-auto" onClick={() => {props.history.push("/sign-in")}}>
                            Sign In
                        </Button>
                    </div>
                </div>
            </div>

            <div className="d-flex flex-column max-container gap-5 mb-5 mt-5">
                <div className="d-flex">
                    <div className="d-flex flex-column w-50">
                        <img className="loader-img" src={APP_LOGO_PATH} alt="Logo" />
                        <span className="mt-2">CivilVertex AI is your complete construction management solution — from client onboarding and 
                            AI-powered estimation to progress tracking and invoice generation.</span>
                    </div>
                    <div style={{ width: "25%" }}>
                        <h5 className="fw-bold">Features</h5>
                        <div className="d-flex flex-column gap-2 mt-3">
                            <span>Client Management</span>
                            <span>AI Estimation</span>
                            <span>Progress Tracking</span>
                            <span>Invoice Generation</span>
                        </div>
                    </div>
                    <div style={{ width: "25%" }}>
                        <h5 className="fw-bold">Resources</h5>
                        <div className="d-flex flex-column gap-2 mt-3">
                            <span><a className="text-decoration-none" href="/user-agreement">User Agreement</a></span>
                            <span>Documentation</span>
                            <span>API Reference</span>
                            <span>Support</span>
                            <span>Contact Us</span>
                            
                        </div>
                    </div>
                </div>
                <hr />
                <div className="fw-bold text-center">
                    © 2026 CivilVertex AI. AI-powered construction management.
                </div>
            </div>
        </div>
    )
}